/**
 * Placeholder variable support for Prompts v1.
 *
 * A placeholder in a prompt body is a curly-brace token containing a single
 * identifier, e.g. `{topic}`, `{Audience}`, `{author_name}`. When the user
 * runs a prompt that contains placeholders, the fill-in dialog asks for a
 * value for each unique variable (case-insensitive de-dup), and every
 * occurrence is replaced before the prompt is pushed to AI Chat.
 *
 * Notes:
 * - Only `{name}` is recognized — no `{{name}}`, no `{name:type}`, no
 *   defaults. Keep this strict so plain prose with stray braces doesn't
 *   accidentally turn into a variable.
 * - Identifiers must start with a letter and may contain letters, digits,
 *   and underscores. Hyphens are not allowed.
 */

const VAR_RE = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

/**
 * Return the ordered list of unique variable names found in `body`. Order is
 * first-appearance; uniqueness is case-insensitive (first casing wins).
 */
export const parseVariables = (body: string): string[] => {
  const seen = new Map<string, string>(); // lower → original casing
  const ordered: string[] = [];
  for (const match of body.matchAll(VAR_RE)) {
    const raw = match[1];
    const key = raw.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, raw);
      ordered.push(raw);
    }
  }
  return ordered;
};

/**
 * Substitute every `{name}` occurrence with the matching value. Lookup is
 * case-insensitive. Unknown variables are left untouched so the user sees
 * exactly what was missing.
 */
export const applyVariables = (body: string, values: Record<string, string>): string => {
  const lower: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) lower[k.toLowerCase()] = v;
  return body.replace(VAR_RE, (full, name: string) => {
    const v = lower[name.toLowerCase()];
    return typeof v === "string" ? v : full;
  });
};
